import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { DetailField, DetailFields } from '../../../../components/basic/DetailFields';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { wireInstructionSelectors } from '../../../../data/global/selectors/admin/cashieringSelectors';
import { formatDate } from '../../../../utilities/date';
import { CashieringDocReview } from './CashieringDocReview';
import { CustomerAccountDetails } from './CustomerAccountDetails';
import styled from 'styled-components';

// Styled components matching the admin cashiering detail component
const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(var(--gap) / 2);
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .loading-text {
    margin-left: 0.75rem;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.75rem;
  }
`;

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap);
`;

const SectionCard = styled.div`
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const SectionHeader = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  border-bottom: 1px solid var(--lighten);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const SectionContent = styled.div`
  padding: var(--gap);
  background: var(--blue-dark);
  color: #fff;

  /* Consistent text styling */
  label {
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
    font-size: 0.98rem;
  }

  .mute {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.98rem;
    font-weight: 400;
  }

  /* Override prop-title and prop-value styling for cashiering */
  .prop-title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
  }

  .prop-value {
    font-size: 0.98rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }

  /* Ensure prop-stacked layout works correctly */
  .prop-stacked {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--gap);
  width: 100%;
  margin: 0;
`;

const GridHeader = styled.div`
  grid-column: 1 / -1;
  font-size: 1.1rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const DetailsRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1em 0;
  border-bottom: 1px solid var(--lighten);
  &:last-child {
    border-bottom: none;
  }
  @media (max-width: 700px) {
    flex-direction: column;
    gap: 0.25em;
  }
`;
const DetailsLabel = styled.div`
  flex: 0 0 180px;
  font-weight: 600;
  color: #fff;
  margin-right: 2em;
  @media (max-width: 700px) {
    margin-right: 0;
    margin-bottom: 0.25em;
  }
`;
const DetailsValue = styled.div`
  flex: 1 1 auto;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.98rem;
  font-weight: 400;
  word-break: break-word;
`;
const SubHeader = styled.div`
  font-weight: 700;
  color: #fff;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-size: 1.05em;
`;

export const CashieringTransferInstructionDetails: React.FunctionComponent<{
  id: number;
  accountId: number;
  isFetching: boolean;
}> = ({ id, accountId, isFetching }) => {
  const wireInstruction = useStateSelector(s => wireInstructionSelectors.data(s, id));
  const isFetchingDetails = useStateSelector(
    s => s.private.cashiering.wireInstructionsDetails[id] && s.private.cashiering.wireInstructionsDetails[id].isFetching
  );

  // Compose fields for the wire instruction details
  const detailRows = useMemo(() => {
    if (!wireInstruction) return [];
    const rows = [
      ['Bank Account Number', wireInstruction.account],
      ['Bank Name', wireInstruction.name],
      ['ABA/Routing Number', wireInstruction.recipientBank.identifier],
      ['Nickname', wireInstruction.nickName],
      ['Identifier Type', wireInstruction.recipientBank.identifierType],
      ['Create Date', formatDate(dayjs(wireInstruction.createDate))],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
    return rows;
  }, [wireInstruction]);
  const intermediaryRows = useMemo(() => {
    if (!wireInstruction || !wireInstruction.intermediary) return [];
    const i = wireInstruction.intermediary;
    return [
      ['Intermediary Bank Name', i.name],
      ['Intermediary Account Number', i.account],
      [
        'Intermediary Address',
        [i.streetAddress1, i.streetAddress2, i.city, i.stateProvince, i.postalCode, i.country]
          .filter(Boolean)
          .join(', '),
      ],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
  }, [wireInstruction]);

  return (
    <DetailWrapper>
      {(isFetching || isFetchingDetails) && (
        <LoadingContainer>
          <div className="loading-text">Loading wire instruction details...</div>
        </LoadingContainer>
      )}
      {!isFetching && !isFetchingDetails && (
        <>
          <SectionCard>
            <SectionContent>
              <DetailsList className="screen-body-section">
                {detailRows.map(([label, value], i) => (
                  <DetailsRow key={i}>
                    <DetailsLabel>{label}</DetailsLabel>
                    <DetailsValue className="mute">{value}</DetailsValue>
                  </DetailsRow>
                ))}
                {intermediaryRows.length > 0 && (
                  <>
                    <SubHeader>Intermediary Bank</SubHeader>
                    {intermediaryRows.map(([label, value], i) => (
                      <DetailsRow key={'intermediary-' + i}>
                        <DetailsLabel>{label}</DetailsLabel>
                        <DetailsValue className="mute">{value}</DetailsValue>
                      </DetailsRow>
                    ))}
                  </>
                )}
              </DetailsList>
            </SectionContent>
          </SectionCard>

          {id && (
            <SectionCard>
              <SectionHeader>
                <h3>
                  <i className="fal fa-file-alt" style={{ marginRight: '8px' }}></i>
                  Document Review
                </h3>
              </SectionHeader>
              <SectionContent>
                <CashieringDocReview id={id} accountId={accountId} />
              </SectionContent>
            </SectionCard>
          )}

          <CustomerAccountDetails accountId={accountId} useCardLayout={true} />
        </>
      )}
    </DetailWrapper>
  );
};
